import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useCategories } from 'pages/integrations/utilities'
import { useIntegrationStatusTextFormatter } from 'pages/integrations/components/status'
import { integrationStatuses } from 'pages/integrations/constants'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import { basicApplied, advancedApplied } from 'components/filter/utilities'
import { compact, prune } from 'utilities/array'
import Paragraph from 'components/typography/paragraph'
import StatusLabel from 'pages/integrations/components/status/label'
import Filter from 'components/filter'

const IntegrationsFilter = ({ filter, setFilter, items, ...props }) => {
    const {
        formatMessage,
        formatDisplayName
    } = useIntl()

    const { categories } = useCategories()
    const valueLabelFormatter = useIntegrationStatusTextFormatter()

    const integrationCountryCodes = prune(items.map(({ meta }) => meta.country))

    const basicFields = [
        {
            type: 'search',
            name: 'search',
            controlProps: {
                placeholder: formatMessage({
                    id: 'integration_action_find_by_name',
                    defaultMessage: 'Find integration by name'
                })
            }
        }
    ]

    const advancedFields = compact([
        {
            type: 'radiobuttons',
            name: 'status',
            label: formatMessage({
                id: 'noun_status',
                defaultMessage: 'Status'
            }),
            getValueLabel: valueLabelFormatter,
            field: {
                options: [
                    {
                        value: 'any',
                        name: (
                            <Paragraph className="small compact">
                                <FormattedMessage
                                    id="statuses_all"
                                    defaultMessage="All statuses" />
                            </Paragraph>
                        )
                    },
                    ...integrationStatuses.map(status => ({
                        value: status,
                        name: <StatusLabel status={status} />
                    }))
                ],
                defaultValue: 'any'
            }
        },
        {
            type: 'checkboxes',
            name: 'country',
            label: formatMessage({
                id: 'address_label_country',
                defaultMessage: 'Country'
            }),
            field: {
                options: [
                    {
                        id: 'none',
                        name: `🌐 ${formatMessage({
                            id: 'integration_country_international',
                            defaultMessage: 'International'
                        })}`
                    },
                    ...integrationCountryCodes.map(countryCode => ({
                        id: countryCode,
                        name: `${getUnicodeFlagIcon(countryCode)} ${formatDisplayName(countryCode, { type: 'region' })}`
                    }))
                ]
            }
        },
        {
            type: 'checkboxes',
            name: 'category',
            label: formatMessage({
                id: 'noun_category',
                defaultMessage: 'Category'
            }),
            field: {
                options: categories.map(({
                    value: id,
                    text: name,
                    ...category
                }) => ({
                    ...category,
                    id,
                    name
                }))
            },
            empty: []
        }
    ])

    return (
        <Filter
            {...props}
            filter={filter}
            setFilter={setFilter}
            basicFields={basicFields}
            advancedFields={advancedFields}
            items={items}
            fetching={false}
            hasFetched={true}
            salt="integrations"
            key={JSON.stringify(categories)} />
    )
}

const basicNames = ['search']
const advancedNames = ['status', 'country', 'category']

export const areBasicFiltersApplied = basicApplied(basicNames)
export const areAdvancedFiltersApplied = advancedApplied(advancedNames)

export const areFiltersApplied = filter => (
    areBasicFiltersApplied(filter) ||
    areAdvancedFiltersApplied(filter)
)

export default IntegrationsFilter